import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

export type ConfirmDialogTone = "default" | "warning" | "danger";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmDialogTone;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const confirmColor = (tone: ConfirmDialogTone) => {
  if (tone === "danger") return "error" as const;
  if (tone === "warning") return "warning" as const;
  return "primary" as const;
};

/**
 * Shared confirmation UI — replaces native window.confirm across admin pages.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  loading = false,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
    <DialogContent>
      {typeof message === "string" ? (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      ) : (
        message
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={loading}>
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        color={confirmColor(tone)}
        onClick={onConfirm}
        disabled={loading}
      >
        {loading ? "Please wait…" : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ConfirmDialog;
